import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { LoadingComponent, components } from '@blaze-cms/react-page-builder';
import { handleLogin, checkIfLoggedIn } from '@blaze-cms/core-auth-ui';
import { useEcommerceContext } from '@blaze-cms/ecommerce-context';
import { useRouter } from 'next/router';
import FormInput from '../FormInput';
import { CREATE_USER } from '../../services/mutations';
import { GET_COUNTRIES, GET_TITLES, GET_COUNTIES_BY_COUNTRY_ID } from '../../services/querys';
import { buildAddressInput, validatePassword } from './helpers';
import { getInitFormState, CLASSNAME } from './constants';
import { validateForm } from '../helpers';
import buildSelectOptions from '../helpers/build-select-options';
import { REGISTER_EMAIL_KEY } from '../../constants';

const { code: Code } = components;

const INIT_FORM_STATE = getInitFormState({
  company: { isRequired: true },
  jobTitle: { isRequired: true }
});

const RegistrationForm = ({
  modifier,
  children,
  privacyPolicyUrl,
  termsAndConditionsUrl,
  redirectUrl
}) => {
  const { isLoggedIn } = checkIfLoggedIn();
  const router = useRouter();
  const { updateCartId } = useEcommerceContext();
  const [countryId, setCoutryId] = useState('');
  const formRef = useRef(null);
  const [formValues, setFormValues] = useState(INIT_FORM_STATE);
  const [showSubmitButton, setShowSubmitButton] = useState(true);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showSuccessMessage, setShowSuccesMessage] = useState(false);

  const { data: { countries = [] } = {}, countriesLoading } = useQuery(GET_COUNTRIES);
  const { data: { titles = [] } = {} } = useQuery(GET_TITLES);
  const { data: { counties = [] } = {} } = useQuery(GET_COUNTIES_BY_COUNTRY_ID, {
    variables: { countryId },
    skip: countriesLoading
  });

  useEffect(
    () => {
      if (!isLoggedIn) return;

      if (redirectUrl) router.push('Resolver', redirectUrl);

      router.push('Resolver', router.asPath);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoggedIn, redirectUrl]
  );

  useEffect(() => {
    setFormValues(prev => {
      const shouldBeRequired = counties && counties.length > 0;
      if (prev.county.isRequired === shouldBeRequired) {
        return prev;
      }
      return {
        ...prev,
        county: {
          ...prev.county,
          isRequired: shouldBeRequired,
          value: shouldBeRequired ? prev.county.value : ''
        }
      };
    });
  }, [counties]);

  useEffect(() => {
    const regEmail = localStorage.getItem(REGISTER_EMAIL_KEY);

    if (regEmail) {
      setFormValues({
        ...formValues,
        email: { ...formValues.email, value: regEmail }
      });
      localStorage.removeItem(REGISTER_EMAIL_KEY);
    }
  }, [formValues]);

  const [createUser, { loading, client }] = useMutation(CREATE_USER, {
    context: { credentials: 'include' },
    onCompleted: async data => {
      setShowSubmitButton(false);
      setShowErrorMessage(false);
      setShowSuccesMessage(true);
      const { cartId } = data.registerUser.loginResult;

      await handleLogin(data.registerUser, client);
      updateCartId(cartId);
    },
    onError: error => {
      setShowErrorMessage(error.message);
      setShowSuccesMessage(false);
    }
  });

  const handleValueChange = (key, value) => {
    let parsedValue = value;
    const updatedValues = { ...formValues };

    if (typeof value === 'object' && value.checked !== undefined) {
      parsedValue = value.checked;
    } else if (typeof value === 'string') {
      parsedValue = value.trim();
    }

    if (key === 'password') {
      const isValid = validatePassword(parsedValue);

      updatedValues.password = {
        ...updatedValues.password,
        error: !isValid
      };
    }

    if (key === 'passwordConfirm') {
      updatedValues.passwordConfirm = {
        ...updatedValues.passwordConfirm,
        error: parsedValue !== updatedValues.password.value
      };
    }

    if (key === 'country') {
      const { id: newCountryId } = countries.find(({ code }) => code === value);
      updatedValues.county = {
        ...updatedValues.county,
        value: ''
      };
      setCoutryId(newCountryId);
    }
    if (key === 'county') {
      const { name: cName } = counties.find(({ code }) => code === value);

      updatedValues.countyName = {
        ...updatedValues.countyName,
        value: cName
      };
    }

    setFormValues({
      ...updatedValues,
      [key]: { ...updatedValues[key], value: parsedValue }
    });
  };

  const handleRegister = () => {
    if (loading) return;

    createUser({
      variables: {
        input: buildAddressInput(formValues)
      }
    });
  };

  const handleSubmit = () => {
    const [isValid, validatedFormData] = validateForm(formValues);
    if (isValid) handleRegister();
    else {
      setFormValues(validatedFormData);
      scrollToError();
    }
  };

  const scrollToError = () => {
    setTimeout(() => {
      const firstErrorElement = formRef.current.querySelector('.error');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        firstErrorElement.focus();
      }
    }, 100);
  };

  const parsedModifier = modifier ? ` ${modifier}` : '';
  const formClassName = `blaze-user-form${parsedModifier}`;
  const parsedCountries = buildSelectOptions(countries);
  const parsedCounties = buildSelectOptions(counties);
  const parsedTitles = buildSelectOptions(titles, 'id');

  if (isLoggedIn) return null;

  return (
    <>
      {!showSuccessMessage && (
        <form className={`${formClassName} ${CLASSNAME}`} ref={formRef}>
          <span className="blaze-user-form__sub_header">Your login</span>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="email"
              className="blaze-user-form__input-group"
              label="Email"
              data={formValues.email}
              onChange={handleValueChange}
              type="text"
              isRequired
            />
          </div>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="password"
              className="blaze-user-form__input-group"
              label="Password"
              data={formValues.password}
              onChange={handleValueChange}
              type="password"
              isRequired
            />
          </div>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="passwordConfirm"
              className="blaze-user-form__input-group"
              label="Confirm password"
              data={formValues.passwordConfirm}
              onChange={handleValueChange}
              type="password"
              isRequired
            />
          </div>
          <span className="blaze-user-form__sub_header">Your details</span>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="title"
              className="blaze-user-form__input-group"
              label="Title"
              data={formValues.title}
              type="select"
              options={parsedTitles}
              isRequired
              onChange={handleValueChange}
            />
            <FormInput
              id="firstname"
              className="blaze-user-form__input-group"
              label="First name"
              data={formValues.firstname}
              isRequired
              type="text"
              onChange={handleValueChange}
            />
            <FormInput
              id="lastname"
              className="blaze-user-form__input-group"
              label="Last name"
              data={formValues.lastname}
              onChange={handleValueChange}
              type="text"
              isRequired
            />
          </div>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="company"
              className="blaze-user-form__input-group"
              label="Company / University"
              data={formValues.company}
              onChange={handleValueChange}
              type="text"
              isRequired
            />
          </div>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="jobTitle"
              className="blaze-user-form__input-group"
              label="Job title"
              data={formValues.jobTitle}
              onChange={handleValueChange}
              type="text"
              isRequired
            />
          </div>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="tel"
              className="blaze-user-form__input-group"
              label="Telephone"
              data={formValues.tel}
              onChange={handleValueChange}
              type="text"
              isRequired
            />
          </div>
          <span className="blaze-user-form__sub_header">Address</span>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="address1"
              className="blaze-user-form__input-group"
              label="Address line 1"
              data={formValues.address1}
              type="text"
              isRequired
              onChange={handleValueChange}
            />
          </div>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="address2"
              className="blaze-user-form__input-group"
              label="Address line 2"
              data={formValues.address2}
              type="text"
              onChange={handleValueChange}
            />
          </div>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="city"
              className="blaze-user-form__input-group"
              label="City / Town"
              data={formValues.city}
              type="text"
              isRequired
              onChange={handleValueChange}
            />
            <FormInput
              id="postcode"
              className="blaze-user-form__input-group"
              label="Zip / Post Code"
              data={formValues.postcode}
              type="text"
              isRequired
              onChange={handleValueChange}
            />
          </div>
          <div className="blaze-user-form__input-group">
            <FormInput
              id="country"
              className="blaze-user-form__input-group"
              label="Country"
              data={formValues.country}
              type="select"
              options={parsedCountries}
              isRequired
              onChange={handleValueChange}
            />
            <FormInput
              id="county"
              className="blaze-user-form__input-group"
              label="County / State"
              data={formValues.county}
              type="select"
              options={parsedCounties}
              isRequired={formValues.county.isRequired}
              onChange={handleValueChange}
            />
          </div>
          {/* <FormInput
            id="tocAccepted"
            className="blaze-user-form__checkbox_input"
            data={formValues.tocAccepted}
            type="checkbox"
            onChange={handleValueChange}
            privacyPolicyUrl={privacyPolicyUrl}
            termsAndConditionsUrl={termsAndConditionsUrl}
          /> */}
          <FormInput
            id="newsletterOptOut"
            className="blaze-user-form__checkbox_input"
            data={formValues.newsletterOptOut}
            type="checkbox"
            onChange={handleValueChange}
            label={
              <>
                We will occasionally email you with news about our other products and services,
                special offers, exclusive content, and events which, based on your dealings with us,
                we feel may be of interest to you. If you do not wish to receive this, please tick
                this box.
                <br /> <br />
                If you do not tick the box, you can still change your mind at any time by selecting
                the &quot;unsubscribe&quot; link located on the bottom of any marketing email we
                send to you. Please see our <a href={privacyPolicyUrl}> Privacy Policy</a> for more
                information about how we process your personal data.
              </>
            }
          />

          {showSubmitButton && (
            <button
              className="blaze-user-form__submit-button"
              type="button"
              onClick={handleSubmit}
              disabled={loading}>
              {loading ? <LoadingComponent /> : 'Continue'}
            </button>
          )}
          {showErrorMessage && (
            <div className="blaze-user-form error_message_wrapper">
              <div className="error_message_icon" />
              <div className="error_message">
                <Code code={showErrorMessage} />
              </div>
            </div>
          )}
        </form>
      )}
      {showSuccessMessage && children}
    </>
  );
};

RegistrationForm.propTypes = {
  modifier: PropTypes.string,
  children: PropTypes.any,
  privacyPolicyUrl: PropTypes.string,
  termsAndConditionsUrl: PropTypes.string,
  redirectUrl: PropTypes.string
};

RegistrationForm.defaultProps = {
  modifier: '',
  children: null,
  privacyPolicyUrl: null,
  termsAndConditionsUrl: null,
  redirectUrl: null
};

export default RegistrationForm;
